import React, { useState, useEffect } from 'react';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';
import { showErrorMsg } from '../../config/common';
import QuarterSelector from './QuarterSelector';

// Types
interface ReportSectionProps {
  title: string;
  open: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}

interface StatRow {
  label: string;
  us: number | string;
  them: number | string;
  usKpiColor?: 'green' | 'yellow' | 'orange' | 'red' | 'none';
}

interface StatSection {
  title: string;
  rows: StatRow[];
}

interface KPITargets {
  possession: {
    possessionPercent: number;
    groundBallsPP: number;
    maxTurnoversPP: number;
  };
  offense: {
    minShotsPP: number;
    minGoalsPP: number;
    shotPercent: number;
    emosPercent: number;
  };
  defense: {
    maxShotsAPP: number;
    savePercent: number;
    mddsPercent: number;
  };
  clearsRides: {
    clearPercent: number;
    minRidesPP: number;
  };
  faceoffs: {
    faceoffPercent: number;
  };
}

// Reusable Components
const ReportSection: React.FC<ReportSectionProps> = ({ title, open, onToggle, children }) => {
  return (
    <div className="shadow-[0_4px_16px_0_rgba(0,0,0,0.25)]">
      <button
        className="w-full flex items-center justify-between px-6 py-3 text-lg font-bold text-white bg-[#05396b] hover:bg-[#19133c] transition-colors focus:outline-none"
        onClick={onToggle}
        type="button"
      >
        <span className="text-xl font-bold">{title}</span>
        <div className="bg-[#7fe526] text-black">
          <svg
            className={`w-5 h-5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      {open && children && <div>{children}</div>}
    </div>
  );
};

const StatTable: React.FC<{ rows: StatRow[]; isLoading?: boolean }> = ({ rows, isLoading }) => {
  const kpiColorBg = {
    green: 'bg-[#c5e5dc]',
    yellow: 'bg-[#f5eac4]',
    orange: 'bg-[#f5eac4]',
    red: 'bg-[#ffa4a7]',
    none: '',
  };

  return (
    <div className="overflow-x-auto relative">
      {/* Loading Spinner */}
      {isLoading && (
        <div role="status" className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2 z-50">
          <svg
            aria-hidden="true"
            className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span className="sr-only">Loading...</span>
        </div>
      )}
      <div className={`${isLoading ? 'opacity-20 pointer-events-none' : ''}`}>
        <table className="min-w-full text-center table-fixed">
          <colgroup>
            <col style={{ width: '50%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
          </colgroup>
          <thead>
            <tr className="text-black">
              <th className="px-2 py-1 border border-[#22c55e] text-base font-semibold bg-white"></th>
              <th className="py-1 border border-[#22c55e] text-base font-bold bg-white">Us</th>
              <th className="py-1 border border-[#22c55e] text-base font-bold bg-white">Them</th>
            </tr>
          </thead>
          <tbody className="text-lg">
            {rows.map((row, index) => {
              const bgColor = index % 2 === 0 ? 'bg-white' : 'bg-[#edf5fc]';
              return (
                <tr key={row.label}>
                  <td className={`px-1 py-1 text-left border border-[#22c55e] text-nowrap ${bgColor}`}>{row.label}</td>
                  <td
                    className={`py-1 border text-xl border-[#22c55e] text-nowrap font-bold ${
                      row.usKpiColor && row.usKpiColor !== 'none' ? kpiColorBg[row.usKpiColor] : bgColor
                    }`}
                    style={{ position: 'relative' }}
                  >
                    {row.us}
                  </td>
                  <td
                    className={`py-1 border text-xl border-[#22c55e] text-nowrap font-bold ${bgColor}`}
                    style={{ position: 'relative' }}
                  >
                    {row.them}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Main Component
const GameTeamsPage: React.FC = () => {
  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();
  const gameId = sessionManager.getGameId();

  const [periods, setPeriods] = useState<string[]>([]);
  const [activeQuarter, setActiveQuarter] = useState('Totals');
  const [openSections, setOpenSections] = useState<string[]>(['Possession']);
  const [shotClockEnabled, setShotClockEnabled] = useState(false);
  const [statSections, setStatSections] = useState<StatSection[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [kpiTargetsLoaded, setKpiTargetsLoaded] = useState(false);
  const [kpiTargets, setKpiTargets] = useState<KPITargets>({
    possession: { possessionPercent: 0, groundBallsPP: 0, maxTurnoversPP: 0 },
    offense: { minShotsPP: 0, minGoalsPP: 0, shotPercent: 0, emosPercent: 0 },
    defense: { maxShotsAPP: 0, savePercent: 0, mddsPercent: 0 },
    clearsRides: { clearPercent: 0, minRidesPP: 0 },
    faceoffs: { faceoffPercent: 0 },
  });

  // Helper function to calculate KPI color based on performance vs target (suppressed when target <= 0)
  const getKpiColor = (
    current: number | string,
    target: number | string,
    isInverse: boolean = false,
  ): 'green' | 'yellow' | 'orange' | 'red' | 'none' => {
    const curNum = Number(current);
    const tgtNum = Number(target);
    if (!isFinite(curNum) || !isFinite(tgtNum) || tgtNum <= 0) return 'none';
    const ratio = isInverse ? tgtNum / curNum : curNum / tgtNum;
    if (!isFinite(ratio)) return 'none';
    if (ratio >= 1.0) return 'green'; // ≥100% - Achieving or exceeding target
    if (ratio >= 0.85) return 'yellow'; // 85-99% - Close to target
    if (ratio >= 0.8) return 'orange'; // 80-84% - Below target
    return 'red'; // <80% - Significantly below target
  };

  // Helper function to get the current period count for per-period calculations
  const getPeriodCount = (): number => {
    if (activeQuarter === 'Totals') {
      // For totals within a single game, calculate based on periods available
      // Exclude "Totals" from the count
      const gamePeriodsCount = periods.filter((p) => p !== 'Totals').length;
      return gamePeriodsCount > 0 ? gamePeriodsCount : 4; // Default to 4 if no periods info
    }
    return 1; // For individual quarters/periods
  };

  // Helper to generate initial empty state
  const getInitialStatSections = (): StatSection[] => {
    const basePossessionRows: StatRow[] = [
      { label: 'Possession Time', us: '-', them: '-' },
      { label: 'Possession %', us: '-', them: '-' },
      { label: 'Possession Count', us: '-', them: '-' },
      { label: 'Avg. Time/Poss.', us: '-', them: '-' },
      { label: 'Poss. Efficiency', us: '-', them: '-' },
      { label: 'Ground Balls', us: '-', them: '-' },
      { label: 'Caused Turnovers', us: '-', them: '-' },
      { label: 'Turnovers', us: '-', them: '-' },
    ];

    // Add shot clock violations if shot clock is enabled
    if (shotClockEnabled) {
      basePossessionRows.push({ label: 'Shot Clock Vs.', us: '-', them: '-' });
    }

    return [
      { title: 'Possession', rows: basePossessionRows },
      {
        title: 'Offense',
        rows: [
          { label: 'Shots', us: '-', them: '-' },
          { label: 'Goals', us: '-', them: '-' },
          { label: 'Shot %', us: '-', them: '-' },
          { label: 'EMOS', us: '-', them: '-' },
          { label: 'EMOF', us: '-', them: '-' },
          { label: 'EMOS %', us: '-', them: '-' },
        ],
      },
      {
        title: 'Defense',
        rows: [
          { label: 'Shots Allowed', us: '-', them: '-' },
          { label: 'MDDS', us: '-', them: '-' },
          { label: 'MDDF', us: '-', them: '-' },
          { label: 'MDDS %', us: '-', them: '-' },
          { label: 'Blocked Shots', us: '-', them: '-' },
        ],
      },
      {
        title: 'Goalie',
        rows: [
          { label: 'Shots on Goal', us: '-', them: '-' },
          { label: 'Saves', us: '-', them: '-' },
          { label: 'Save %', us: '-', them: '-' },
        ],
      },
      {
        title: 'Clears / Rides',
        rows: [
          { label: 'Clear Attempts', us: '-', them: '-' },
          { label: 'Clears', us: '-', them: '-' },
          { label: 'Clear %', us: '-', them: '-' },
          { label: 'Ride Wins', us: '-', them: '-' },
        ],
      },
      {
        title: 'Face-offs',
        rows: [
          { label: 'Face-off Wins', us: '-', them: '-' },
          { label: 'Face-off Losses', us: '-', them: '-' },
          { label: 'Face-off %', us: '-', them: '-' },
        ],
      },
    ];
  };

  // Helper to map API response to StatSection[]
  const mapStatsToSections = (us: any, them: any): StatSection[] => {
    // Helper to format time display
    const formatTime = (seconds: number | undefined): string => {
      if (typeof seconds !== 'number' || isNaN(seconds)) return '0:0';
      const mins = Math.floor(seconds / 60);
      const secs = Math.round(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const periodCount = getPeriodCount();
    const isTotals = activeQuarter === 'Totals';
    const isIndividualPeriod = !isTotals;

    // Base possession rows
    const basePossessionRows: StatRow[] = [
      {
        label: 'Possession Time',
        us: us.possessionTime ? formatTime(Number(us.possessionTime)) : '0:0',
        them: them.possessionTime ? formatTime(Number(them.possessionTime)) : '0:0',
      },
      {
        label: 'Possession %',
        us: us.possessionPercent !== undefined ? `${Math.round(us.possessionPercent)}%` : '-',
        them: them.possessionPercent !== undefined ? `${Math.round(them.possessionPercent)}%` : '-',
        usKpiColor:
          us.possessionPercent !== undefined
            ? getKpiColor(us.possessionPercent, kpiTargets.possession.possessionPercent)
            : 'none',
      },
      {
        label: 'Possession Count',
        us: us.possessionCount ?? '-',
        them: them.possessionCount ?? '-',
      },
      {
        label: 'Avg. Time/Poss.',
        us: us.avgTimePerPossession !== undefined ? formatTime(us.avgTimePerPossession) : '-',
        them: them.avgTimePerPossession !== undefined ? formatTime(them.avgTimePerPossession) : '-',
      },
      {
        label: 'Poss. Efficiency',
        us: us.possessionEfficiency !== undefined ? `${Math.round(us.possessionEfficiency)}%` : '-',
        them: them.possessionEfficiency !== undefined ? `${Math.round(them.possessionEfficiency)}%` : '-',
      },
      {
        label: 'Ground Balls',
        us: us.groundballs ?? '-',
        them: them.groundballs ?? '-',
        usKpiColor:
          us.groundballs !== undefined && them.groundballs !== undefined
            ? getKpiColor(
                (us.groundballs * 100) / (us.groundballs + (them.groundballs || 0)),
                kpiTargets.possession.groundBallsPP,
              )
            : 'none',
      },
      {
        label: 'Caused Turnovers',
        us: us.causedturnover ?? '-',
        them: them.causedturnover ?? '-',
      },
      {
        label: 'Turnovers',
        us: us.turnovers ?? '-',
        them: them.turnovers ?? '-',
        usKpiColor:
          us.turnovers !== undefined
            ? getKpiColor(
                // For individual periods, compare directly to per-period target
                // For totals, scale target by number of periods in the game
                isIndividualPeriod ? us.turnovers : us.turnovers / periodCount,
                kpiTargets.possession.maxTurnoversPP,
                true,
              )
            : 'none',
      },
    ];

    // Add shot clock violations if shot clock is enabled
    if (shotClockEnabled) {
      basePossessionRows.push({
        label: 'Shot Clock Vs.',
        us: us.shotClockViolations ?? '-',
        them: them.shotClockViolations ?? '-',
      });
    }

    return [
      {
        title: 'Possession',
        rows: basePossessionRows,
      },
      {
        title: 'Offense',
        rows: [
          {
            label: 'Shots',
            us: us.shots ?? '-',
            them: them.shots ?? '-',
            usKpiColor:
              us.shots !== undefined
                ? getKpiColor(
                    // For individual periods, compare directly to per-period target
                    // For totals, divide by period count to get per-period average
                    isIndividualPeriod ? us.shots : us.shots / periodCount,
                    kpiTargets.offense.minShotsPP,
                  )
                : 'none',
          },
          {
            label: 'Goals',
            us: us.goals ?? '-',
            them: them.goals ?? '-',
            usKpiColor:
              us.goals !== undefined
                ? getKpiColor(
                    // For individual periods, compare directly to per-period target
                    // For totals, divide by period count to get per-period average
                    isIndividualPeriod ? us.goals : us.goals / periodCount,
                    kpiTargets.offense.minGoalsPP,
                  )
                : 'none',
          },
          {
            label: 'Shot %',
            us: us.shotPercent !== undefined ? `${Math.round(us.shotPercent)}%` : '-',
            them: them.shotPercent !== undefined ? `${Math.round(them.shotPercent)}%` : '-',
            usKpiColor:
              us.shotPercent !== undefined ? getKpiColor(us.shotPercent, kpiTargets.offense.shotPercent) : 'none',
          },
          {
            label: 'SOG%',
            us: us.sogPercent !== undefined ? `${us.sogPercent}%` : '-',
            them: them.sogPercent !== undefined ? `${them.sogPercent}%` : '-',
          },
          { label: 'EMOS', us: us.emos ?? '-', them: them.emos ?? '-' },
          { label: 'EMOF', us: us.emof ?? '-', them: them.emof ?? '-' },
          {
            label: 'EMOS %',
            us: us.emosPercent !== undefined ? `${Math.round(us.emosPercent)}%` : '-',
            them: them.emosPercent !== undefined ? `${Math.round(them.emosPercent)}%` : '-',
            usKpiColor:
              us.emosPercent !== undefined ? getKpiColor(us.emosPercent, kpiTargets.offense.emosPercent) : 'none',
          },
        ],
      },
      {
        title: 'Defense',
        rows: [
          {
            label: 'Shots Allowed',
            us: us.shotsallowed ?? '-',
            them: them.shotsallowed ?? '-',
            usKpiColor:
              us.shotsallowed !== undefined
                ? getKpiColor(
                    // For individual periods, compare directly to per-period target
                    // For totals, divide by period count to get per-period average
                    isIndividualPeriod ? us.shotsallowed : us.shotsallowed / periodCount,
                    kpiTargets.defense.maxShotsAPP,
                    true,
                  )
                : 'none',
          },
          { label: 'MDDS', us: us.mdds ?? '-', them: them.mdds ?? '-' },
          { label: 'MDDF', us: us.mddf ?? '-', them: them.mddf ?? '-' },
          {
            label: 'MDDS %',
            us: us.mddsPercent !== undefined ? `${Math.round(us.mddsPercent)}%` : '-',
            them: them.mddsPercent !== undefined ? `${Math.round(them.mddsPercent)}%` : '-',
            usKpiColor:
              us.mddsPercent !== undefined ? getKpiColor(us.mddsPercent, kpiTargets.defense.mddsPercent) : 'none',
          },
          {
            label: 'Blocked Shots',
            us: us.blocked ?? '-',
            them: them.blocked ?? '-',
          },
        ],
      },
      {
        title: 'Goalie',
        rows: [
          {
            label: 'Shots on Goal',
            us: us.shotsongoal ?? '-',
            them: them.shotsongoal ?? '-',
          },
          { label: 'Saves', us: us.saved ?? '-', them: them.saved ?? '-' },
          {
            label: 'Save %',
            us: us.savePercent !== undefined ? `${Math.round(us.savePercent)}%` : '-',
            them: them.savePercent !== undefined ? `${Math.round(them.savePercent)}%` : '-',
            usKpiColor:
              us.savePercent !== undefined ? getKpiColor(us.savePercent, kpiTargets.defense.savePercent) : 'none',
          },
        ],
      },
      {
        title: 'Clears / Rides',
        rows: [
          {
            label: 'Clear Attempts',
            us: us.clearattempts ?? '-',
            them: them.clearattempts ?? '-',
          },
          { label: 'Clears', us: us.clears ?? '-', them: them.clears ?? '-' },
          {
            label: 'Clear %',
            us: us.clearPercent !== undefined ? `${Math.round(us.clearPercent)}%` : '-',
            them: them.clearPercent !== undefined ? `${Math.round(them.clearPercent)}%` : '-',
            usKpiColor:
              us.clearPercent !== undefined
                ? getKpiColor(us.clearPercent, kpiTargets.clearsRides.clearPercent)
                : 'none',
          },
          {
            label: 'Ride Wins',
            us: us.ridewins ?? '-',
            them: them.ridewins ?? '-',
            usKpiColor:
              us.ridewins !== undefined
                ? getKpiColor(
                    // For individual periods, compare directly to per-period target
                    // For totals, divide by period count to get per-period average
                    isIndividualPeriod ? us.ridewins : us.ridewins / periodCount,
                    kpiTargets.clearsRides.minRidesPP,
                  )
                : 'none',
          },
        ],
      },
      {
        title: 'Face-offs',
        rows: [
          {
            label: 'Face-off Wins',
            us: us.faceoffwins ?? '-',
            them: them.faceoffwins ?? '-',
          },
          {
            label: 'Face-off Losses',
            us: us.faceofflosses ?? '-',
            them: them.faceofflosses ?? '-',
          },
          {
            label: 'Face-off %',
            us: us.faceoffPercent !== undefined ? `${Math.round(us.faceoffPercent)}%` : '-',
            them: them.faceoffPercent !== undefined ? `${Math.round(them.faceoffPercent)}%` : '-',
            usKpiColor:
              us.faceoffPercent !== undefined
                ? getKpiColor(us.faceoffPercent, kpiTargets.faceoffs.faceoffPercent)
                : 'none',
          },
        ],
      },
    ];
  };

  // Fetch client data to get halfPeriod
  useEffect(() => {
    const fetchClient = async () => {
      if (!clientId || !seasonId || !gameId) return;

      try {
        const response = await api.get(`${API_URLS.GET_GAME_PERIODS}/${gameId}/periods`, {
          params: { clientId, seasonId },
        });

        if (response.data.success) {
          const data = response.data.data;
          const hp = data.halfPeriod;
          const shotClock = data.shotClock;
          const otFlag = data.otFlag;

          setShotClockEnabled(!!shotClock);

          if (hp === true) {
            if (otFlag) setPeriods(['Totals', 'H1', 'H2', 'OT']);
            else setPeriods(['Totals', 'H1', 'H2']);
          } else {
            if (otFlag) setPeriods(['Totals', 'Q1', 'Q2', 'Q3', 'Q4', 'OT']);
            else setPeriods(['Totals', 'Q1', 'Q2', 'Q3', 'Q4']);
          }
          setActiveQuarter('Totals');
          // Stats will be fetched by the useEffect when shotClockEnabled changes
        } else {
          showErrorMsg(response.data.message || 'Failed to fetch client data');
        }
      } catch (error: any) {
        showErrorMsg('Failed to fetch client data');
      }
    };
    fetchClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  // Update stat sections when shot clock enabled state changes
  useEffect(() => {
    if (shotClockEnabled !== undefined && kpiTargetsLoaded) {
      getTeamsStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shotClockEnabled]);

  // Fetch KPI targets when component mounts or gameId changes
  useEffect(() => {
    if (clientId && seasonId) {
      setKpiTargetsLoaded(false); // Reset loaded state
      fetchKpiTargets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, seasonId, gameId]);

  // Fetch stats when KPI targets are loaded
  useEffect(() => {
    if (kpiTargetsLoaded && clientId && seasonId && gameId) {
      getTeamsStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpiTargetsLoaded, activeQuarter, gameId]);

  // Fetch KPI targets
  const fetchKpiTargets = async () => {
    try {
      let url = `${API_URLS.GET_GAME_KPI_TARGETS}?clientId=${clientId}&seasonId=${seasonId}`;
      if (gameId && gameId !== 'all') {
        url += `&gameId=${gameId}`;
      }
      const response = await api.get(url);
      if (response.data.success) {
        setKpiTargets(response.data.data);
      }
      setKpiTargetsLoaded(true);
    } catch (err) {
      console.error('Failed to fetch KPI targets:', err);
      setKpiTargetsLoaded(true); // Still mark as loaded even on error
    }
  };

  const getTeamsStats = async () => {
    if (!clientId || !seasonId || !gameId) {
      setStatSections(getInitialStatSections());
      return;
    }

    // Wait for KPI targets to be loaded before fetching stats
    if (!kpiTargetsLoaded) {
      return;
    }

    setIsRefreshing(true);
    try {
      const response = await api.get(
        `${API_URLS.GET_SEASON_STATS}?clientId=${clientId}&seasonId=${seasonId}&quarterId=${activeQuarter}&gameId=${gameId}`,
      );
      if (response.data.success) {
        const { us, them } = response.data.data;
        setStatSections(mapStatsToSections(us, them));
      } else {
        // Set empty state when no data available
        setStatSections(getInitialStatSections());
        showErrorMsg(response.data.message || 'Failed to fetch teams information');
      }
    } catch (err: any) {
      // Set empty state on error
      setStatSections(getInitialStatSections());
      showErrorMsg(err.response?.data?.message || err.message || 'Failed to fetch teams information');
    } finally {
      // Reset refreshing state after animation completes
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  };

  const handleSectionToggle = (title: string) => {
    setOpenSections((prev) => (prev.includes(title) ? prev.filter((section) => section !== title) : [...prev, title]));
  };

  return (
    <div className="w-full">
      <QuarterSelector periods={periods} activeQuarter={activeQuarter} onSelectQuarter={setActiveQuarter} />

      <div className="border border-[#bccedf] overflow-auto">
        <div className="relative flex flex-col justify-between border-b border-[#bccedf] z-10">
          <div className="absolute top-0 bottom-0 left-1/4 w-px bg-[#bccedf] z-0" />
          <div className="absolute top-0 bottom-0 left-3/4 w-px bg-[#bccedf] z-0" />

          <div className="flex justify-center w-full py-4">
            <img src="/icons/icon-up.png" alt="up" className="w-12 h-12" />
          </div>

          <div className="absolute top-0 left-0 w-full h-full flex flex-row items-center justify-between text-[30px] px-4 font-bold text-start">
            <p className="text-[#05396b]">Teams Report</p>
            <div
              className={`w-8 h-8 rounded-md p-1 cursor-pointer ${isRefreshing ? 'bg-gray-400' : 'bg-[#7fe526]'}`}
              onClick={
                isRefreshing
                  ? undefined
                  : async () => {
                      if (!kpiTargetsLoaded) {
                        await fetchKpiTargets();
                      }
                      getTeamsStats();
                    }
              }
            >
              <img
                src="/icons/icon-refresh.png"
                alt="refresh"
                className={`w-full h-full ${isRefreshing ? 'opacity-50' : ''}`}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center w-full py-4 px-2">
          <div className="flex flex-col gap-3 max-w-[600px] w-full">
            {statSections.map((section) => (
              <ReportSection
                key={section.title}
                title={section.title}
                open={openSections.includes(section.title)}
                onToggle={() => handleSectionToggle(section.title)}
              >
                <StatTable rows={section.rows} isLoading={isRefreshing} />
              </ReportSection>
            ))}
          </div>
        </div>

        <div className="flex relative border-t border-[#bccedf] justify-center w-full z-10 py-4">
          <div className="absolute top-0 bottom-0 left-1/4 w-px bg-[#bccedf] z-0" />
          <div className="absolute top-0 bottom-0 left-3/4 w-px bg-[#bccedf] z-0" />
          <img src="/icons/icon-down.png" alt="down" className="w-12 h-12" />
        </div>
      </div>
    </div>
  );
};

export default GameTeamsPage;
